import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";

import BasicCli from "../../../main/components/basicCli/BasicCli";
import GeneralModal from "../../../main/components/modal/general/GeneralModal";
import GeneralSelect from "../../../main/components/select/GeneralSelect";
import GeneralInput from "../../../main/components/generalInput/GeneralInput";
import axiosInstance from "../../../main/initializers/axios";
import { ApiConstants } from "../../../main/constants/ApiConstants";

import IRoom from "../../../main/interfaces/IRoom";
import IHotel from "../../../main/interfaces/IHotel";
import {
  loadEntityGalleryMap,
  getMetadataFileUrl,
  EntityFileMetadata,
} from "../../../main/utils/entityGallery";

import "./Rooms.css";
import "../HotelShared/HotelShared.css";
import i18n from "../../../main/initializers/i18n";

export const Floors = [
  { id: 1, name: "Floor 1" },
  { id: 2, name: "Floor 2" },
  { id: 3, name: "Floor 3" },
  { id: 4, name: "Floor 4" },
  { id: 5, name: "Floor 5" },
  { id: 6, name: "Floor 6" },
  { id: 7, name: "Floor 7" },
  { id: 8, name: "Floor 8" },
  { id: 9, name: "Floor 9" },
  { id: 10, name: "Floor 10" },
];

type RoomStatus = {
  label: string;
  class: string;
};

export const RoomsContent = ({
  embedded = false,
  tabsBar,
}: {
  embedded?: boolean;
  tabsBar?: React.ReactNode;
}) => {
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [hotels, setHotels] = useState<IHotel[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<IRoom | null>(null);
  const [roomPhotoMap, setRoomPhotoMap] = useState<
    Record<number, EntityFileMetadata[]>
  >({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<number | null>(null);
  const roomStatuses = useMemo<Record<number, RoomStatus>>(
    () => ({
      1: { label: i18n.t("room.available", { defaultValue: "Available" }), class: "room-free" },
      2: { label: i18n.t("room.occupied", { defaultValue: "Occupied" }), class: "room-busy" },
      3: { label: i18n.t("room.cleaning", { defaultValue: "Cleaning" }), class: "room-cleaning" },
      4: { label: i18n.t("room.maintenance", { defaultValue: "Maintenance" }), class: "room-maint" },
    }),
    []
  );

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      hotelId: "",
      roomTypeId: "",
      roomNumber: "",
      code: "",
      floor: "",
      status: "1",
      hasSeaView: false,
      hasBalcony: false,
      notes: "",
    },
  });

  const hotelId = watch("hotelId");
  const selectedStatus = Number(watch("status") || 1);

  const loadData = async () => {
    try {
      const [hotelRes, roomRes, roomTypeRes] = await Promise.all([
        axiosInstance.get(ApiConstants.hotel.hotels),
        axiosInstance.get(ApiConstants.hotel.rooms),
        axiosInstance.get(ApiConstants.hotel.roomTypes),
      ]);

      setHotels(hotelRes.data || []);
      setRooms(roomRes.data || []);
      setRoomTypes(roomTypeRes.data || []);
      const roomItems = roomRes.data || [];
      const galleryMap = await loadEntityGalleryMap(
        roomItems.map((room: IRoom) => room.id).filter(Boolean),
        ApiConstants.EntityTypes.Room,
        5,
      );
      setRoomPhotoMap(galleryMap);
    } catch (err: any) {
      toast.error(err?.response?.data || i18n.t("room.errorLoading"));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedRoom?.id) {
      reset({
        hotelId: selectedRoom.hotelId?.toString(),
        roomTypeId: selectedRoom.roomTypeId?.toString(),
        roomNumber: selectedRoom.roomNumber,
        code: selectedRoom.code,
        floor: selectedRoom.floor?.toString(),
        status: (selectedRoom.status ?? 1).toString(),
        hasSeaView: selectedRoom.hasSeaView,
        hasBalcony: selectedRoom.hasBalcony,
        notes: selectedRoom.notes,
      });
    }
  }, [selectedRoom, reset]);

  useEffect(() => {
    setSelectedFiles([]);
  }, [selectedRoom?.id]);

  const roomsByFloor = useMemo(() => {
    const filtered = rooms.filter(
      (r) => !hotelId || r.hotelId === Number(hotelId),
    );

    const floors: any = {};

    filtered.forEach((room) => {
      if (!floors[room.floor]) floors[room.floor] = [];

      floors[room.floor].push(room);
    });

    return floors;
  }, [rooms, hotelId]);

  const deleteRoom = async () => {
    if (!selectedRoom?.id) return;

    if (
      !window.confirm(
        i18n.t("room.confirmDelete", { room: selectedRoom.roomNumber }),
      )
    )
      return;

    try {
      await axiosInstance.delete(ApiConstants.hotel.roomDelete, {
        params: { id: selectedRoom.id },
      });

      toast.success(i18n.t("room.roomDeleted"));
      setSelectedRoom(null);

      loadData();
    } catch (err: any) {
      toast.error(err?.response?.data || i18n.t("room.errorDeleting"));
    }
  };
  const filteredRoomTypes = useMemo(() => {
    if (!hotelId) return [];

    return roomTypes.filter((x) => x.hotelId === Number(hotelId));
  }, [roomTypes, hotelId]);

  const saveRoom = async (data: any) => {
    try {
      const payload = {
        ...selectedRoom,
        hotelId: Number(data.hotelId),
        roomTypeId: Number(data.roomTypeId),
        roomNumber: data.roomNumber,
        code: data.code || "RM-" + data.roomNumber,
        floor: Number(data.floor),
        status: Number(data.status || selectedRoom?.status || 1),
        hasSeaView: data.hasSeaView,
        hasBalcony: data.hasBalcony,
        notes: data.notes,
      };

      await axiosInstance.post(ApiConstants.hotel.roomSave, payload);

      toast.success(
        selectedRoom?.id
          ? i18n.t("room.roomUpdated")
          : i18n.t("room.roomCreated"),
      );

      setSelectedRoom(null);

      loadData();
    } catch (err: any) {
      toast.error(err?.response?.data || i18n.t("room.errorSaving"));
    }
  };

  const handleRoomPhotoSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const uploadRoomPhotos = async () => {
    if (!selectedRoom?.id) return;

    const existingPhotos = roomPhotoMap[selectedRoom.id] || [];
    if (!selectedFiles.length) {
      toast.warning(i18n.t("product.selectFileWarning"));
      return;
    }

    if (existingPhotos.length + selectedFiles.length > 5) {
      toast.warning("A room can have maximum 5 photos.");
      return;
    }

    try {
      setIsUploadingPhotos(true);

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("EntityId", String(selectedRoom.id));
        formData.append("EntityType", String(ApiConstants.EntityTypes.Room));
        await axiosInstance.post(ApiConstants.file.upload, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success("Room photos uploaded successfully.");
      setSelectedFiles([]);
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data || "Failed to upload room photos.");
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const deleteRoomPhoto = async (photoId: number) => {
    if (!selectedRoom?.id) return;
    if (!window.confirm("Delete this photo from the room gallery?")) return;

    try {
      setDeletingPhotoId(photoId);
      await axiosInstance.delete(ApiConstants.file.deleteById(photoId));
      toast.success("Room photo deleted.");
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data || "Failed to delete room photo.");
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const getRoomPhotos = (roomId?: number) =>
    roomPhotoMap[Number(roomId || 0)] || [];

  return (
      <div className={`hotelSectionPage ${embedded ? "roomsPageEmbedded" : ""}`}>
        <div className="ui-panel hotelSectionHeader">
          {embedded ? (
            <div className="hotelInfoEmbeddedRow">
              <div className="hotelInfoEmbeddedTabs">{tabsBar}</div>
              <div className="hotelSectionActions">
                <div className="hotelInfoToolbarField">
                  <GeneralSelect
                    label={i18n.t("hotel.hotel")}
                    name="hotelId"
                    control={control}
                    errors={errors}
                    data={hotels}
                    value="name"
                    preSelectedVal={i18n.t("hotel.selectHotel")}
                  />
                </div>
                <Button
                  variant="contained"
                  className="hotelInfoPrimaryAction"
                  onClick={() => {
                    setSelectedRoom({} as IRoom);
                    reset();
                  }}
                >
                  {i18n.t("room.addRoom")}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="hotelSectionHeading">
                <h2>{i18n.t("room.title")}</h2>
                <p>{i18n.t("room.subtitle")}</p>
              </div>

              <div className="hotelSectionActions">
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectedRoom({} as IRoom);
                    reset();
                  }}
                >
                  {i18n.t("room.addRoom")}
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="bcCard hotelSectionCard hotelRoomsCard">
          {!embedded && (
            <div className="roomsFilter hotelToolbar">
              <GeneralSelect
                label={i18n.t("hotel.hotel")}
                name="hotelId"
                control={control}
                errors={errors}
                data={hotels}
                value="name"
                preSelectedVal={i18n.t("hotel.selectHotel")}
              />
            </div>
          )}

          <div className="hotelStack">
            {Object.entries(roomsByFloor).map(([floor, floorRooms]: any) => (
              <section key={floor} className="bcCard floorSection">
                <div className="floorSectionHeader">
                  <h3>
                    {i18n.t("room.floor")} {floor}
                  </h3>
                  <span>
                    {floorRooms.length} {i18n.t("room.rooms")}
                  </span>
                </div>

                <div className="roomsGrid">
                  {floorRooms.map((room: IRoom) => {
                    const status = roomStatuses[room.status];

                    return (
                      <div
                        key={room.id}
                        className={`roomCard ${status?.class}`}
                        onClick={() => {
                          setSelectedRoom(room);
                        }}
                      >
                        <div className="roomPhotoStrip">
                          {getRoomPhotos(room.id).length > 0 ? (
                            <>
                              <img
                                src={getMetadataFileUrl(
                                  getRoomPhotos(room.id)[0]?.id,
                                )}
                                alt={room.roomNumber}
                                className="roomPhotoCover"
                              />
                              <span className="roomPhotoCount">
                                +{getRoomPhotos(room.id).length}
                              </span>
                            </>
                          ) : (
                            <div className="roomPhotoPlaceholder">
                              No photos
                            </div>
                          )}
                        </div>

                        <div className="roomHeader">
                          <span className="roomNumber">{room.roomNumber}</span>
                        </div>

                        <div className="roomType">{room.roomType?.name}</div>

                        <div className="roomStatus">{status?.label}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        {selectedRoom && (
          <GeneralModal
            handleClose={() => {
              setSelectedRoom(null);
            }}
            headerName={
  selectedRoom.id
    ? `${i18n.t("room.editRoom")} ${selectedRoom.roomNumber}`
    : i18n.t("room.createRoom")
}
            width="min(1280px, calc(100vw - 24px))"
            height="min(900px, calc(100dvh - 24px))"
            radius="24px"
            hideFooter
          >
            <form onSubmit={handleSubmit(saveRoom)} className="roomEditForm">
                <div className="roomFormSection">
                  <h4 className="roomFormTitle">
                    {i18n.t("room.identifiers")}
                  </h4>

                  <div className="roomFormGrid">
                    <GeneralInput
                      label={i18n.t("room.roomNumber")}
                      name="roomNumber"
                      control={control}
                      errors={errors}
                      type="number"
                      placeholder="Room Number"
                    />

                    <GeneralInput
                      label={i18n.t("room.roomCode")}
                      name="code"
                      control={control}
                      errors={errors}
                      type="text"
                      placeholder="Room Code"
                    />
                  </div>

                  <div className="roomStatusSelector">
                    <span className="roomStatusSelectorLabel">
                      {i18n.t("generic.status")}
                    </span>
                    <div className="roomActions roomActions--edit">
                      {Object.entries(roomStatuses).map(([statusKey, status]) => {
                        const statusValue = Number(statusKey);
                        const isActive = selectedStatus === statusValue;

                        return (
                          <Button
                            key={statusKey}
                            type="button"
                            variant={isActive ? "contained" : "outlined"}
                            className={`roomStatusButton ${status.class} ${
                              isActive ? "roomStatusButton--active" : ""
                            }`}
                            onClick={() =>
                              setValue("status", statusValue.toString(), {
                                shouldDirty: true,
                              })
                            }
                          >
                            {status.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="roomFormSection">
                  <h4 className="roomFormTitle">
                    {i18n.t("room.roomInformation")}
                  </h4>

                  <GeneralSelect
                    label="Hotel"
                    name="hotelId"
                    control={control}
                    errors={errors}
                    data={hotels}
                    value="name"
                  />

                  <GeneralSelect
                    label={i18n.t("room.roomType")}
                    name="roomTypeId"
                    control={control}
                    errors={errors}
                    data={filteredRoomTypes}
                    value="name"
                  />

                  <GeneralSelect
                    label="Floor"
                    name="floor"
                    control={control}
                    errors={errors}
                    data={Floors}
                    value="name"
                  />
                </div>

                {selectedRoom?.id ? (
                  <div className="roomFormSection roomGalleryEditorSection">
                    <div className="roomGalleryHeader roomGalleryHeaderInForm">
                      <div>
                        <h4 className="roomFormTitle roomGalleryTitle">
                          <PhotoLibraryOutlinedIcon fontSize="small" />
                          <span>Room photos</span>
                        </h4>
                        <p className="roomGallerySubtext">
                          Manage up to 5 gallery photos for this room.
                        </p>
                      </div>
                      <span className="roomGalleryBadge">
                        {getRoomPhotos(selectedRoom.id).length}/5
                      </span>
                    </div>

                    <div className="roomGalleryGrid roomGalleryGridEditor">
                      {getRoomPhotos(selectedRoom.id).map((photo) => (
                        <div key={photo.id} className="roomGalleryCard">
                          <img
                            src={getMetadataFileUrl(photo.id)}
                            alt={`Room ${selectedRoom.roomNumber}`}
                            className="roomGalleryImage"
                          />
                          <button
                            type="button"
                            className="roomGalleryDelete"
                            onClick={() => deleteRoomPhoto(photo.id)}
                            disabled={deletingPhotoId === photo.id}
                            aria-label="Delete room photo"
                            title="Delete room photo"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                      ))}

                      {!getRoomPhotos(selectedRoom.id).length && (
                        <div className="roomGalleryEmpty roomGalleryEmptyEditor">
                          No room photos yet.
                        </div>
                      )}
                    </div>

                    {getRoomPhotos(selectedRoom.id).length < 5 && (
                      <div className="roomUploadPanel roomUploadPanelEditor">
                        <label className="roomUploadField">
                          <CloudUploadOutlinedIcon fontSize="small" />
                          <span>
                            {selectedFiles.length
                              ? `${selectedFiles.length} file(s) selected`
                              : "Choose room photos"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleRoomPhotoSelection}
                          />
                        </label>
                        <div className="roomUploadMeta">
                          <span>
                            Select images and save them directly to this room
                            gallery.
                          </span>
                          <Button
                            variant="contained"
                            onClick={uploadRoomPhotos}
                            disabled={
                              !selectedFiles.length || isUploadingPhotos
                            }
                          >
                            {isUploadingPhotos
                              ? "Uploading..."
                              : "Upload photos"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="roomModalFooter">
                  <Button
                    variant="outlined"
                    type="button"
                    className="roomActionBtn"
                    color={selectedRoom?.id ? "error" : "inherit"}
                    onClick={() => {
                      if (selectedRoom?.id) {
                        void deleteRoom();
                        return;
                      }

                      setSelectedRoom(null);
                    }}
                  >
                    {selectedRoom?.id
                      ? i18n.t("generic.delete")
                      : i18n.t("generic.cancel")}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    className="roomActionBtn"
                  >
                    {i18n.t("generic.save")}
                  </Button>
                </div>
            </form>
          </GeneralModal>
        )}
      </div>
  );
};

const Rooms = () => (
  <BasicCli>
    <RoomsContent />
  </BasicCli>
);

export default Rooms;
